import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box } from '@material-ui/core';
import { enqueueSnackbar } from 'notistack';
import { SBase, SButton } from '@setvi/shared/components';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { AutoRFQQueryKey, getRfqQuery } from 'Services';
import { RfqInternalShareEntry } from 'Services/Query/Auto-RFQ/Types';
import {
  addInternalShareMutation,
  removeInternalShareMutation
} from 'Services/Mutations/Auto-RFQ';
import { RfqInternalShareResponse } from 'Services/Mutations/Auto-RFQ/types';
import { getQuoteV2Query, QuotesV2QueryKey } from 'Services/Query/QuotesV2';
import {
  addQuoteInternalShareMutation,
  removeQuoteInternalShareMutation
} from 'Services/Mutations/QuotesV2';
import { ShareManager } from './share-manager';
import type { PendingShareUser } from './share-manager';

export const INTERNAL_SHARE_DIALOG = 'rfq-internal-share-dialog';

interface InternalShareDialogProps {
  /** Share against an RFQ. Mutually exclusive with `quoteId`. */
  rfqId?: string;
  /** Share against a quote directly — used for quotes with no linked RFQ.
   * Mutually exclusive with `rfqId`. */
  quoteId?: string;
  onClose: () => void;
  /** Wording for the shared entity — "RFQ" on the Auto RFQ page, "Quote" when
   * opened from Quotes V2. */
  entityLabel?: string;
}

const errorMessage = (status: number | undefined, entityLabel: string) => {
  if (status === 403) return 'Cannot share with users outside your company';
  if (status === 401)
    return `You don't have access to share this ${entityLabel}`;
  if (status === 404) return `This ${entityLabel} no longer exists`;
  return 'Something went wrong. Please try again.';
};

export const InternalShareDialog = ({
  rfqId,
  quoteId,
  onClose,
  entityLabel = 'RFQ'
}: InternalShareDialogProps) => {
  const { user } = useAppContext();
  const queryClient = useQueryClient();
  // Quote mode is used for standalone quotes (no linked RFQ); RFQ mode handles
  // RFQs and RFQ-linked quotes (which mirror the RFQ's share list server-side).
  const isQuote = !rfqId && !!quoteId;
  // Teammates picked from search but not shared yet — committed via "Share".
  const [pendingUsers, setPendingUsers] = useState<PendingShareUser[]>([]);
  // Existing recipients staged for removal — committed via the footer button.
  const [removedUserIds, setRemovedUserIds] = useState<string[]>([]);
  const [isCommitting, setIsCommitting] = useState(false);

  // Both read from the cache the detail page already populated; the share list
  // rides the existing GET, so no extra round-trip in the common case.
  const { data: rfq } = useQuery({
    ...getRfqQuery(rfqId || ''),
    enabled: !!rfqId
  });
  const { data: quote } = useQuery({
    ...getQuoteV2Query(quoteId || ''),
    enabled: isQuote
  });

  const owner = isQuote ? quote?.user : rfq?.user;
  const ownerId = owner?._id != null ? String(owner._id) : undefined;

  // The shared list only carries recipients; the creator isn't part of it. Show
  // the creator as an "Owner" row, but only once the entity is actually shared
  // (i.e. more than one person has access) — a private entity falls back to the
  // "Only you can see this" empty state.
  const entries: RfqInternalShareEntry[] = useMemo(() => {
    const shared: RfqInternalShareEntry[] =
      (isQuote ? quote?.internalShare : rfq?.internalShare) || [];
    const ownerInShared = !!ownerId && shared.some(e => e.userId === ownerId);

    if (!ownerId || ownerInShared || shared.length === 0) return shared;

    // RFQ owner carries a single `name`; quote owner has split name fields.
    let ownerEntry: RfqInternalShareEntry | null = null;
    if (isQuote && quote?.user) {
      ownerEntry = {
        userId: ownerId,
        email: quote.user.email,
        firstName: quote.user.firstName || '',
        lastName: quote.user.lastName || ''
      };
    } else if (!isQuote && rfq?.user) {
      const [firstName = '', ...rest] = (rfq.user.name || '').trim().split(' ');
      ownerEntry = {
        userId: ownerId,
        email: rfq.user.email,
        firstName,
        lastName: rest.join(' ')
      };
    }

    return ownerEntry ? [ownerEntry, ...shared] : shared;
  }, [
    isQuote,
    quote?.internalShare,
    quote?.user,
    rfq?.internalShare,
    rfq?.user,
    ownerId
  ]);

  const { mutateAsync: addRfqShare } = useMutation(addInternalShareMutation());
  const { mutateAsync: removeRfqShare } = useMutation(
    removeInternalShareMutation()
  );
  const { mutateAsync: addQuoteShare } = useMutation(
    addQuoteInternalShareMutation()
  );
  const { mutateAsync: removeQuoteShare } = useMutation(
    removeQuoteInternalShareMutation()
  );

  const addShare = (email: string) =>
    isQuote
      ? addQuoteShare({ quoteId: quoteId as string, email })
      : addRfqShare({ rfqId: rfqId as string, email });

  const removeShare = (userId: string) =>
    isQuote
      ? removeQuoteShare({ quoteId: quoteId as string, userId })
      : removeRfqShare({ rfqId: rfqId as string, userId });

  // Server is the source of truth — replace the cached list with the returned
  // array rather than optimistically patching (in RFQ mode the server also
  // mirrors the change to the linked quote).
  const applyList = (res: RfqInternalShareResponse) => {
    const cacheKey = isQuote
      ? [QuotesV2QueryKey.GET_QUOTE, quoteId]
      : [AutoRFQQueryKey.GET_SINGLE, rfqId];
    queryClient.setQueryData(cacheKey, (old: any) => ({
      ...old,
      internalShare: res?.internalShare || []
    }));
  };

  // Toggle a recipient in/out of the staged-removal set (the ✕ / undo button).
  const toggleRemoveUser = (userId: string) =>
    setRemovedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );

  // Stage a teammate (deduped by email — same person can't be added twice).
  const addPendingUser = (newUser: PendingShareUser) =>
    setPendingUsers(prev =>
      prev.some(p => p.email.toLowerCase() === newUser.email.toLowerCase())
        ? prev
        : [...prev, newUser]
    );

  // Drop a single staged teammate (the ✕ on a pending row).
  const removePendingUser = (email: string) =>
    setPendingUsers(prev =>
      prev.filter(p => p.email.toLowerCase() !== email.toLowerCase())
    );

  const hasChanges = pendingUsers.length > 0 || removedUserIds.length > 0;

  // Commit all staged changes from the footer button: remove the staged
  // recipients, then add the staged teammates. Run sequentially so the list the
  // server returns each call stays consistent; apply the final one.
  const handleApply = async () => {
    if (!hasChanges) return;
    setIsCommitting(true);
    try {
      let latest: RfqInternalShareResponse | undefined;
      await removedUserIds.reduce(
        (chain, userId) =>
          chain.then(async () => {
            latest = (await removeShare(userId)) as RfqInternalShareResponse;
          }),
        Promise.resolve()
      );
      await pendingUsers.reduce(
        (chain, pendingUser) =>
          chain.then(async () => {
            latest = (await addShare(
              pendingUser.email
            )) as RfqInternalShareResponse;
          }),
        Promise.resolve()
      );
      if (latest) applyList(latest);
      setPendingUsers([]);
      setRemovedUserIds([]);
      onClose();
    } catch (err: any) {
      enqueueSnackbar(errorMessage(err?.response?.status, entityLabel), {
        variant: 'error'
      });
    } finally {
      setIsCommitting(false);
    }
  };

  let primaryLabel = 'Share';
  if (pendingUsers.length > 0) {
    primaryLabel = isCommitting ? 'Sharing…' : 'Share';
  } else if (removedUserIds.length > 0) {
    primaryLabel = isCommitting ? 'Saving…' : 'Save';
  }

  return (
    <SBase
      style={{ background: '#FFFFFF' }}
      header={{
        title: `Share this ${entityLabel}`,
        onClose,
        divider: true
      }}
      width="560px"
      height="auto">
      <ShareManager
        entityLabel={entityLabel}
        entries={entries}
        ownerId={ownerId}
        currentUserId={user?.ID != null ? String(user.ID) : undefined}
        currentUserEmail={user?.Email}
        pendingUsers={pendingUsers}
        removedUserIds={removedUserIds}
        onSelectPending={addPendingUser}
        onRemovePending={removePendingUser}
        onToggleRemove={toggleRemoveUser}
      />

      {/* Custom footer — buttons grouped on the right. */}
      <Box
        mt={3}
        pt={2}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12
        }}>
        <SButton
          variant="outlined"
          color="inherit"
          style={{ borderRadius: 99 }}
          onClick={onClose}>
          Close
        </SButton>
        <SButton
          variant="contained"
          style={{ borderRadius: 99 }}
          disabled={!hasChanges || isCommitting}
          onClick={handleApply}>
          {primaryLabel}
        </SButton>
      </Box>
    </SBase>
  );
};
